/**
 * @author Lorenzo Muscariello <lmuscariello@ciaopeople.com>
 */
import { inEnum, isDefined, isObject, isString } from "@ciaopeople/cp-shatsu/TypeChecking";
import { AdCallParams } from "./AdCallParams.js";
import { VideoInfoRawData } from "../../model/video/VideoInfo.js";

/**
 * Qualifies the type of ad break, based on its occurrence
 * before, during or after the media
 */
export enum AdBreakType {

    PREROLL = "preroll",
    MIDROLL = "midroll",
    POSTROLL = "postroll"
}

/**
 * qualifies the ad unit type
 * if unit is associated with a vast tag or a Vmap playlist
 */
export enum AdUnitType {
    VAST_TAG = "vast",
    VMAP = "vmap"
}

/**
 * AdBreak with common break info
 */
export interface AdBreak {
    label: string,
    breakType: AdBreakType
}


/**
 * type barrier for `AdBreak`
 * @param a value to check
 * @returns 
 */
export function isAdBreak(a: any): a is AdBreak {
    return isObject(a) &&
            ("label" in a && isString(a.label)) &&
            ("breakType" in a && inEnum(a.breakType, AdBreakType));
}


export type AdUnitParams = {
    sz: string,
    iu: string
};

export function isAdUnitParams(x: any): x is AdUnitParams {
    

    return isObject(x) && 
            "sz" in x && isString(x.sz) &&
            "iu" in x && isString(x.iu);
}
/**
 * Requestable AdUnit - thru an ad loader
 * i.e. ima3 AdsLoader
 */
export interface AdUnit  {
    baseUrl: string
    unitType: AdUnitType
    label: string
    params?: AdUnitParams
}


/**
 * type barrier for `AdUnit`
 * 
 * @param a value to check
 * @returns 
 */
export function isAdUnit(a: any): a is AdUnit {
    return isObject(a) &&
        ("label" in a && isString(a.label)) &&
            ("baseUrl" in a  && isString(a.baseUrl)) &&
                ("unitType" in a  && inEnum(a.unitType, AdUnitType)) &&
                    (!("params" in a) || isAdUnitParams(a.params));
}




export interface AdUnitWithParams extends AdUnit {
    params: AdUnitParams
}

export function isAdUnitWithParams(a: any): a is AdUnitWithParams {
    return isAdUnit(a) && isDefined(a.params);
}



export interface VASTTagAdUnit extends AdUnit,AdBreak {
    unitType: AdUnitType.VAST_TAG,
    
}


export function isVASTTagAdUnit(a: any): a is VASTTagAdUnit {
    return isAdUnit(a) && a.unitType == AdUnitType.VAST_TAG && 
        isAdBreak(a);  
}



/**
 * represents an `AdUnit` associated with a VMAP response 
 */
export interface VMAPTagAdUnit extends AdUnit {
    unitType: AdUnitType.VMAP,
    params: AdUnitParams
}

/**
 * type barrier for `VMAPTagAdUnit`
 * @param a value to check
 * @returns 
 */
export function isVMAPTagAdUnit(a: any): a is VMAPTagAdUnit {
    return isAdUnit(a) && a.unitType == AdUnitType.VMAP;
}


/**
 * An `AdUnit` enriched with a parametrized url, typically with context / user information, 
 * that should be used by AdLoaders to request VAST or VMAP tags
 */
export interface ResolvedAdUnit extends AdUnit {
    parametrizedUrl: string
}


/**
 * type barrier for `ResolvedAdUnit`
 * @param a value to check
 * @returns 
 */
export function isResolvedAdUnit(a: any): a is ResolvedAdUnit {
    return isObject(a) &&
            ("resolvedUrl" in a && isString(a.resolvedUrl)) &&
                isAdUnit(a);
}



/**
 * transforms an `AdUnit` in a `ResolvedAdUnit`
 */
export type AdCallResolver = (call: AdUnit, info: VideoInfoRawData, startingParams: AdCallParams) => ResolvedAdUnit



/**
 * `NoOp transformer. simply creates a `ResolvedAdUnit` by copying baseUrl to parametrizedUrl
 * @param call AdUnit to enrich
 * @returns 
 */
export function NoOpAdCallResolver(call: AdUnit,info: VideoInfoRawData,startingParams: AdCallParams = {}): ResolvedAdUnit {
    return {
     ...call,
     parametrizedUrl: call.baseUrl   
    };
}



const PARAMS_EXTRACT_REGEX = new RegExp("[?&]sz=([^&]+)&iu=([^&]+).*$");

export function enrichAdUnitWithParams<A extends AdUnit>(a: A): A {
    if(isAdUnitWithParams(a)) {
        return a;
    }
    else {
        const matches = a.baseUrl.match(PARAMS_EXTRACT_REGEX);
        if(matches && matches.length > 2 && matches[1] !== "" && matches[2] !== "") {
            return {
                ...a,
                params: {
                    sz: matches[1],
                    iu: matches[2]
                }
            }
        }
        else return a;
    }

}
